"use client";

import React, { useState } from "react";
import Link from "next/link";
import {
  Menu,
  MenuItem,
  ServiceItem,
  HoveredLink,
  ProductItem,
} from "./ui/navbar-menu";
import { Logo } from "./Logo";
import { Button } from "./ui/button";
// import {
//   ContactModal,
//   ContactModalContent,
//   ContactModalTrigger,
// } from "./ui/contact-modal";
import {
  Monitor,
  Shield,
  Users,
  BarChart3,
  Menu as MenuIcon,
  Terminal,
  ChevronRight,
  Lock,
} from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/app/components/ui/sheet";
// import { Card, CardContent } from "@/app/components/ui/card";
import { cn } from "@/lib/utils";

const services = [
  {
    title: "Managed IT",
    description:
      "Complete IT infrastructure management with proactive monitoring, support, and maintenance.",
    href: "/services/managed-it",
    icon: Monitor,
  },
  {
    title: "Cyber Security",
    description:
      "Multi-layered protection against threats with advanced monitoring, detection, and rapid response.",
    href: "/services/cyber-security",
    icon: Lock,
  },
  {
    title: "Penetration Testing",
    description:
      "Identify vulnerabilities through simulated attacks to strengthen your security posture.",
    href: "/services/penetration-testing-vulnerability-scanning",
    icon: Users,
  },
  {
    title: "Compliance & Risk Management",
    description:
      "Navigate regulatory requirements and mitigate risks with comprehensive assessment and planning.",
    href: "/services/compliance-risk-management",
    icon: Shield,
  },
];

const navigationItems = [
  { title: "Services", href: "#", hasMegaMenu: true },
  { title: "Blog", href: "/blog", hasMegaMenu: false },
  { title: "About", href: "/about", hasMegaMenu: false },
  { title: "Contact", href: "/contact", hasMegaMenu: false },
];

export default function NewNavigation() {
  const [active, setActive] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);

  return (
    <div className="sticky top-0 z-50 w-full">
      <div className="relative">
        <header className="relative h-16 md:h-20 lg:h-24 bg-slate-950/95 backdrop-blur-sm border-b border-[rgb(var(--color-neon-blue)_/_0.1)]">
          <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:60px_60px] overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-slate-950/80 via-slate-950/50 to-slate-950/80" />
          </div>

          <div className="max-w-8xl mx-auto px-2 sm:px-6 lg:px-8 relative h-full">
            <div className="flex h-full items-center justify-between">
              {/* Mobile Menu */}
              <div className="flex items-center flex-shrink-0">
                <Sheet
                  open={isMobileMenuOpen}
                  onOpenChange={setIsMobileMenuOpen}
                >
                  <SheetTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="mr-2 px-0 text-base hover:bg-transparent focus-visible:bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 lg:hidden p-2"
                    >
                      <MenuIcon className="h-6 w-6 text-[rgb(var(--color-neon-blue))] hover:text-[rgb(var(--color-neon-blue)_/_0.8)]" />
                      <span className="sr-only">Toggle menu</span>
                    </Button>
                  </SheetTrigger>
                  <SheetContent
                    side="left"
                    className="w-[min(80vw,280px)] bg-slate-950/95 backdrop-blur-sm border-r border-[rgb(var(--color-neon-blue)_/_0.1)] p-0 shadow-[0_0_25px_rgba(var(--color-neon-blue),0.2)] overflow-hidden"
                  >
                    {/* Gradient background effect */}
                    <div className="absolute inset-0 pointer-events-none">
                      <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:60px_60px]" />
                      <div className="absolute -top-[300px] -left-[300px] w-[600px] h-[600px] bg-[rgb(var(--color-neon-blue)_/_0.03)] rounded-full blur-[80px]" />
                      <div className="absolute -bottom-[300px] -right-[300px] w-[600px] h-[600px] bg-[rgb(var(--color-electric-blue)_/_0.03)] rounded-full blur-[80px]" />
                    </div>

                    <div className="relative z-10 h-full flex flex-col">
                      <SheetHeader className="border-b border-[rgb(var(--color-neon-blue)_/_0.1)] p-4 mb-2">
                        <SheetTitle asChild>
                          <Link
                            href="/"
                            className="flex items-center"
                            onClick={() => setIsMobileMenuOpen(false)}
                            aria-label="Panoptic IT - Home"
                          >
                            <Logo
                              size="lg"
                              className="!px-0 !py-0 !bg-transparent !border-0 scale-[0.5] md:scale-[0.6]"
                            />
                          </Link>
                        </SheetTitle>
                      </SheetHeader>

                      <div className="flex-1 overflow-y-auto py-3 px-4">
                        <div className="flex flex-col space-y-1">
                          <div className="mb-3">
                            <button
                              onClick={() => setIsServicesOpen(!isServicesOpen)}
                              className="flex items-center justify-between w-full text-base font-medium text-white/90 hover:text-white transition-colors py-2.5 px-3 rounded-md hover:bg-[rgb(var(--color-neon-blue)_/_0.1)]"
                              aria-label="Toggle Services Menu"
                              aria-expanded={isServicesOpen}
                            >
                              <span className="text-base">Services</span>
                              <ChevronRight
                                className={cn(
                                  "h-4 w-4 text-[rgb(var(--color-neon-blue)_/_0.7)] transition-transform duration-200",
                                  isServicesOpen && "rotate-90"
                                )}
                                aria-hidden="true"
                              />
                            </button>
                            <div
                              className={cn(
                                "mt-1 ml-3 pl-3 border-l border-[rgb(var(--color-neon-blue)_/_0.2)] space-y-1 overflow-hidden transition-all duration-200",
                                isServicesOpen
                                  ? "max-h-[400px] opacity-100"
                                  : "max-h-0 opacity-0"
                              )}
                            >
                              {services.map((service) => (
                                <Link
                                  key={service.title}
                                  href={service.href}
                                  onClick={() => setIsMobileMenuOpen(false)}
                                  className="flex items-center gap-2 py-2.5 px-2 text-sm font-medium text-white/70 hover:text-white rounded-md transition-colors hover:bg-[rgb(var(--color-neon-blue)_/_0.05)]"
                                >
                                  <div className="flex-shrink-0 h-7 w-7 bg-[rgb(var(--color-neon-blue)_/_0.1)] rounded-md flex items-center justify-center">
                                    <service.icon className="h-3.5 w-3.5 text-[rgb(var(--color-neon-blue))]" />
                                  </div>
                                  <span>{service.title}</span>
                                </Link>
                              ))}
                            </div>
                          </div>

                          {["Blog", "About", "Contact"].map((item) => (
                            <Link
                              key={item}
                              href={`/${item.toLowerCase()}`}
                              onClick={() => setIsMobileMenuOpen(false)}
                              className="text-base font-medium text-white/90 hover:text-white transition-colors py-2.5 px-3 rounded-md hover:bg-[rgb(var(--color-neon-blue)_/_0.1)]"
                            >
                              {item}
                            </Link>
                          ))}
                        </div>
                      </div>

                      <div className="mt-auto p-4 border-t border-[rgb(var(--color-neon-blue)_/_0.1)]">
                        <div className="grid grid-cols-2 gap-2">
                          <Link
                            href="/client-portal"
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="flex items-center justify-center gap-1 text-sm font-medium text-[rgb(var(--color-neon-blue))] hover:text-[rgb(var(--color-neon-blue-light))] transition-colors py-2 px-2 rounded-md border border-[rgb(var(--color-neon-blue)_/_0.3)] hover:border-[rgb(var(--color-neon-blue)_/_0.5)] bg-[rgb(var(--color-neon-blue)_/_0.05)]"
                            aria-label="Access Client Portal"
                          >
                            Client Portal
                          </Link>
                          <Link
                            href="/discover"
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="flex items-center justify-center gap-1 text-sm font-medium text-black bg-[rgb(var(--color-neon-green))] hover:bg-[rgb(var(--color-neon-green)_/_0.9)] transition-colors py-2 px-2 rounded-md shadow-[0_0_15px_rgba(var(--color-neon-green),0.3)]"
                            aria-label="Contact Panoptic for IT Services"
                          >
                            Contact
                          </Link>
                        </div>
                      </div>
                    </div>
                  </SheetContent>
                </Sheet>

                <Link
                  href="/"
                  className="flex items-center p-2"
                  aria-label="Panoptic IT - Home"
                >
                  <Logo
                    size="sm"
                    className="!px-0 !py-0 !bg-transparent !border-0 scale-[0.6] md:scale-75 lg:scale-100"
                  />
                </Link>
              </div>

              {/* Desktop Menu */}
              <div className="hidden lg:flex flex-1 items-center justify-center min-w-0">
                <Menu setActive={setActive}>
                  <MenuItem
                    setActive={setActive}
                    active={active}
                    item="Services"
                    className="font-medium text-base"
                  >
                    <div className="p-6 rounded-xl w-[80vw] max-w-4xl min-w-[320px] sm:min-w-[480px]">
                      {/* Background glow effects */}
                      <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-xl">
                        <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:60px_60px]" />
                        <div className="absolute -bottom-[250px] right-[250px] w-[400px] h-[400px] bg-[rgb(var(--color-neon-blue)_/_0.03)] rounded-full blur-[80px]" />
                        <div className="absolute -top-[250px] left-[250px] w-[400px] h-[400px] bg-[rgb(var(--color-electric-blue)_/_0.03)] rounded-full blur-[80px]" />
                      </div>

                      {/* Services section title */}
                      <div className="relative z-10 mb-4">
                        <h3 className="text-lg font-semibold text-white/90">
                          Our Services
                        </h3>
                        <div className="h-0.5 w-12 bg-gradient-to-r from-[rgb(var(--color-neon-blue))] to-[rgb(var(--color-neon-green))] mt-2"></div>
                      </div>

                      {/* Service items grid */}
                      <div className="relative z-10">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                          {services.map((service) => (
                            <ServiceItem key={service.title} {...service} />
                          ))}
                        </div>
                      </div>
                    </div>
                  </MenuItem>
                  <HoveredLink href="/blog" className="font-medium text-base">
                    Blog
                  </HoveredLink>
                  <HoveredLink href="/about" className="font-medium text-base">
                    About
                  </HoveredLink>
                  <HoveredLink
                    href="/contact"
                    className="font-medium text-base"
                  >
                    Contact
                  </HoveredLink>
                </Menu>
              </div>

              {/* CTA Button */}
              <div className="flex items-center justify-end gap-2 lg:gap-4 -mr-1 lg:mr-0 flex-shrink-0">
                <Link href="/client-portal" aria-label="Access Client Portal">
                  <Button
                    variant="outline"
                    size="lg"
                    className="hidden lg:flex px-8 text-white/90 hover:text-white border-[rgb(var(--color-neon-blue)_/_0.2)] hover:border-[rgb(var(--color-neon-blue)_/_0.4)] bg-[rgb(var(--color-neon-blue)_/_0.05)] hover:bg-[rgb(var(--color-neon-blue)_/_0.1)] transition-colors duration-200"
                  >
                    Client Portal
                  </Button>
                </Link>
                <Link
                  href="/discover"
                  aria-label="Book a Call with Panoptic IT"
                >
                  <Button
                    variant="neon"
                    size="lg"
                    className="hidden lg:flex px-8 shadow-[0_0_15px_rgba(var(--color-neon-green),0.2)] hover:shadow-[0_0_20px_rgba(var(--color-neon-green),0.3)] transition-all duration-200"
                  >
                    Book a Call
                  </Button>
                  <Button
                    variant="neon"
                    size="sm"
                    className="lg:hidden px-3 py-1.5 text-sm h-8 min-w-0 shadow-[0_0_10px_rgba(var(--color-neon-green),0.2)]"
                  >
                    Contact
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </header>
      </div>
    </div>
  );
}
